// «Знания»: решения (обязательны для всех агентов) и база знаний (заметки, правила, инструкции).
import { useId, useMemo, useState } from 'react'
import { api, qs } from '../api.ts'
import { AgentLabel, CreatedBy } from '../components/AgentAvatar.tsx'
import { AgentSelect } from '../components/AgentSelect.tsx'
import { DirectionChip, ServiceChip } from '../components/chips.tsx'
import { Icon } from '../components/Icon.tsx'
import { Modal } from '../components/Layer.tsx'
import { Badge, Button, Empty, ErrorBox, Field, IconButton, Loading, PageHeader, Tabs, cx } from '../components/ui.tsx'
import { useAsync, useBusy, useDebouncedValue } from '../lib/hooks.ts'
import { KNOWLEDGE_KIND_LABELS, SCOPE_LABELS, formatDateTime, timeAgo } from '../lib/format.ts'
import { Markdown } from '../lib/markdown.tsx'
import { navigate, useSearchParam } from '../router.tsx'
import { useLive, useNow } from '../store.ts'
import { toast, toastError } from '../toast.ts'
import type { Decision, Knowledge, KnowledgeKind } from '../types.ts'
import { DocsTab } from './DocsTab.tsx'

type ScopeType = 'global' | 'direction' | 'service' | 'agent'
type TabId = 'docs' | 'decisions' | 'kb'

export function ScopeLabel({ type, id }: { type: ScopeType; id: string | null }) {
  if (type === 'global' || !id) return <Badge tone="accent">{SCOPE_LABELS.global}</Badge>
  if (type === 'direction') return <DirectionChip id={id} />
  if (type === 'service') return <ServiceChip id={id} />
  return <AgentLabel id={id} size="xs" link />
}

function ScopePicker({
  type,
  id,
  onChange,
  allowAgent,
}: {
  type: ScopeType
  id: string
  onChange: (type: ScopeType, id: string) => void
  allowAgent?: boolean
}) {
  const directions = useLive((s) => s.directions)
  const services = useLive((s) => s.services)
  const sid = useId()
  return (
    <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
      <Field label="Область" htmlFor={sid}>
        <select id={sid} className="ctl" value={type} onChange={(e) => onChange(e.target.value as ScopeType, '')}>
          <option value="global">{SCOPE_LABELS.global}</option>
          <option value="direction">{SCOPE_LABELS.direction}</option>
          <option value="service">{SCOPE_LABELS.service}</option>
          {allowAgent ? <option value="agent">{SCOPE_LABELS.agent}</option> : null}
        </select>
      </Field>
      {type === 'direction' || type === 'service' ? (
        <Field label={type === 'direction' ? 'Направление' : 'Сервис'} htmlFor={`${sid}-id`}>
          <select id={`${sid}-id`} className="ctl" value={id} onChange={(e) => onChange(type, e.target.value)}>
            <option value="">— выбрать —</option>
            {(type === 'direction' ? directions : services).map((x) => (
              <option key={x.id} value={x.id}>
                {x.name}
              </option>
            ))}
          </select>
        </Field>
      ) : type === 'agent' ? (
        <Field label="Агент" htmlFor={`${sid}-id`}>
          <AgentSelect id={`${sid}-id`} value={id} onChange={(v) => onChange('agent', v)} emptyLabel="— выбрать —" />
        </Field>
      ) : null}
    </div>
  )
}

/** /knowledge → справочники (первая вкладка); ?tab=decisions|kb — решения и база знаний; /knowledge/docs/<unit> — справочник. */
export function KnowledgePage({ docUnit = null }: { docUnit?: string | null }) {
  const tabParam = useSearchParam('tab')
  const tab: TabId = docUnit ? 'docs' : tabParam === 'kb' ? 'kb' : tabParam === 'decisions' ? 'decisions' : 'docs'
  return (
    <div className={cx('page', tab !== 'docs' && 'max-w-[1160px]')}>
      <PageHeader
        className="mb-4"
        title="Знания"
        lede="Справочники — как устроен каждый сервис и процессы направлений. Решения — то, что ты уже решил: агенты обязаны их соблюдать. База знаний — факты, правила и инструкции."
      />
      <Tabs<TabId>
        label="Разделы знаний"
        value={tab}
        onChange={(v) => navigate(v === 'docs' ? '/knowledge/docs' : `/knowledge?tab=${v}`)}
        items={[
          { id: 'docs', label: 'Справочники' },
          { id: 'decisions', label: 'Решения' },
          { id: 'kb', label: 'База знаний' },
        ]}
      />
      <div className="mt-5">
        {tab === 'docs' ? <DocsTab unit={docUnit} /> : tab === 'decisions' ? <DecisionsTab /> : <KnowledgeTab />}
      </div>
    </div>
  )
}

// ───────── решения ─────────

function DecisionsTab() {
  const now = useNow()
  const list = useAsync((signal) => api.get<Decision[]>('/api/decisions', { signal }), [])
  const [onlyActive, setOnlyActive] = useState(true)
  const [q, setQ] = useState('')
  const [editing, setEditing] = useState<Decision | 'new' | null>(null)

  const items = useMemo(() => {
    const needle = q.trim().toLowerCase()
    return (list.data ?? [])
      .filter((d) => (!onlyActive || d.active) && (!needle || d.title.toLowerCase().includes(needle) || d.decision.toLowerCase().includes(needle)))
      .sort((a, b) => Number(b.active) - Number(a.active) || b.created_at.localeCompare(a.created_at))
  }, [list.data, onlyActive, q])

  const toggleActive = async (d: Decision) => {
    if (d.active && !window.confirm(`Отключить решение «${d.title}»? Агенты перестанут его учитывать.`)) return
    try {
      const u = await api.patch<Decision>(`/api/decisions/${d.id}`, { active: !d.active })
      list.setData((cur) => cur?.map((x) => (x.id === d.id ? { ...x, ...u } : x)))
      toast(u.active ? 'Решение снова действует' : 'Решение отключено', 'success')
    } catch (e) {
      toastError(e)
    }
  }

  return (
    <div>
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <input
          type="search"
          className="ctl h-8 w-full sm:w-64"
          placeholder="Найти решение…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          aria-label="Поиск решений"
        />
        <label className="inline-flex cursor-pointer items-center gap-1.5 text-[12.5px] text-muted select-none">
          <input
            type="checkbox"
            className="h-4 w-4"
            checked={onlyActive}
            onChange={(e) => setOnlyActive(e.target.checked)}
          />
          Только действующие
        </label>
        <Button variant="primary" icon="plus" className="ml-auto" onClick={() => setEditing('new')}>
          Новое решение
        </Button>
      </div>
      {list.error && !list.data ? (
        <ErrorBox error={list.error} onRetry={list.reload} />
      ) : !list.data ? (
        <Loading />
      ) : items.length === 0 ? (
        <Empty icon="check">Решений пока нет. Они появляются, когда ты отвечаешь агенту и отмечаешь «Сохранить как решение».</Empty>
      ) : (
        <div className="flex flex-col gap-2">
          {items.map((d) => (
            <article key={d.id} className={cx('rounded-[12px] border border-line bg-surface px-[18px] py-3.5 shadow-card', !d.active && 'opacity-60')}>
              <header className="flex items-start gap-2">
                <div className="min-w-0 flex-1">
                  <h3 className="text-[15px] leading-snug font-semibold break-words">{d.title}</h3>
                  <div className="mt-1 flex flex-wrap items-center gap-1.5">
                    <ScopeLabel type={d.scope_type} id={d.scope_id} />
                    {!d.active ? <Badge>отключено</Badge> : null}
                  </div>
                </div>
                <span className="flex shrink-0 items-center gap-1">
                  <IconButton icon="edit" size="sm" label="Изменить решение" onClick={() => setEditing(d)} />
                  <Button size="xs" variant="ghost" onClick={() => void toggleActive(d)}>
                    {d.active ? 'Отключить' : 'Включить'}
                  </Button>
                </span>
              </header>
              <Markdown text={d.decision} className="mt-1.5 text-[13px]" />
              {d.rationale ? (
                <div className="mt-2 border-l-2 border-line pl-2.5 text-[12.5px] text-muted">
                  <div className="lbl">Почему</div>
                  <Markdown text={d.rationale} />
                </div>
              ) : null}
              <footer className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11.5px] text-subtle">
                <span className="inline-flex items-center gap-1">
                  <CreatedBy value={d.created_by} />
                </span>
                <span title={formatDateTime(d.created_at)}>{timeAgo(d.created_at, now)}</span>
                {d.source ? <span className="min-w-0 [overflow-wrap:anywhere]">источник: {d.source}</span> : null}
                {d.escalation_id ? <span>из ответа на вопрос #{d.escalation_id}</span> : null}
              </footer>
            </article>
          ))}
        </div>
      )}
      {editing ? (
        <DecisionModal
          initial={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={(d) =>
            list.setData((cur) => (cur ? (cur.some((x) => x.id === d.id) ? cur.map((x) => (x.id === d.id ? d : x)) : [d, ...cur]) : cur))
          }
        />
      ) : null}
    </div>
  )
}

function DecisionModal({ initial, onClose, onSaved }: { initial: Decision | null; onClose: () => void; onSaved: (d: Decision) => void }) {
  const [title, setTitle] = useState(initial?.title ?? '')
  const [decision, setDecision] = useState(initial?.decision ?? '')
  const [rationale, setRationale] = useState(initial?.rationale ?? '')
  const [scopeType, setScopeType] = useState<ScopeType>(initial?.scope_type ?? 'global')
  const [scopeId, setScopeId] = useState(initial?.scope_id ?? '')
  const [busy, run] = useBusy()
  const f = { title: useId(), dec: useId(), rat: useId() }

  const save = () =>
    run(async () => {
      if (!title.trim() || !decision.trim()) {
        toast('Нужны название и текст решения', 'error')
        return
      }
      if (scopeType !== 'global' && !scopeId) {
        toast('Выбери, к чему относится решение', 'error')
        return
      }
      const body = {
        title: title.trim(),
        decision: decision.trim(),
        rationale: rationale.trim(),
        scope_type: scopeType,
        scope_id: scopeType === 'global' ? null : scopeId,
      }
      try {
        const d = initial
          ? await api.patch<Decision>(`/api/decisions/${initial.id}`, body)
          : await api.post<Decision>('/api/decisions', body)
        onSaved(d)
        toast(initial ? 'Решение обновлено' : 'Решение сохранено — агенты его увидят', 'success')
        onClose()
      } catch (e) {
        toastError(e, 'Не удалось сохранить')
      }
    })

  return (
    <Modal
      title={initial ? 'Изменить решение' : 'Новое решение'}
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Отмена
          </Button>
          <Button variant="primary" loading={busy} onClick={() => void save()}>
            Сохранить
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        <Field label="Название" htmlFor={f.title}>
          <input id={f.title} className="ctl" value={title} onChange={(e) => setTitle(e.target.value)} data-autofocus />
        </Field>
        <Field label="Решение" htmlFor={f.dec} hint="Формулируй как правило: что делать и чего не делать.">
          <textarea id={f.dec} className="ctl min-h-[100px]" value={decision} onChange={(e) => setDecision(e.target.value)} />
        </Field>
        <Field label="Почему (необязательно)" htmlFor={f.rat}>
          <textarea id={f.rat} className="ctl min-h-[60px]" value={rationale} onChange={(e) => setRationale(e.target.value)} />
        </Field>
        <ScopePicker
          type={scopeType}
          id={scopeId}
          onChange={(t, i) => {
            setScopeType(t)
            setScopeId(i)
          }}
        />
      </div>
    </Modal>
  )
}

// ───────── база знаний ─────────

const KINDS = Object.keys(KNOWLEDGE_KIND_LABELS) as KnowledgeKind[]

function KnowledgeTab() {
  const now = useNow()
  const [q, setQ] = useState('')
  const [scope, setScope] = useState<'' | ScopeType>('')
  const [kind, setKind] = useState<'' | KnowledgeKind>('')
  const [editing, setEditing] = useState<Knowledge | 'new' | null>(null)
  const [expanded, setExpanded] = useState<Set<number>>(new Set())
  const query = useDebouncedValue(q.trim(), 300)

  const list = useAsync(
    (signal) => api.get<Knowledge[]>(`/api/knowledge${qs({ q: query, scope_type: scope, kind })}`, { signal }),
    [query, scope, kind],
    { keep: true },
  )

  const items = useMemo(
    () => [...(list.data ?? [])].sort((a, b) => Number(b.pinned) - Number(a.pinned) || (query ? 0 : b.updated_at.localeCompare(a.updated_at))),
    [list.data, query],
  )

  const patch = async (k: Knowledge, body: Partial<Knowledge>) => {
    try {
      const u = await api.patch<Knowledge>(`/api/knowledge/${k.id}`, body)
      list.setData((cur) => cur?.map((x) => (x.id === k.id ? { ...x, ...u } : x)))
    } catch (e) {
      toastError(e)
    }
  }

  const remove = async (k: Knowledge) => {
    if (!window.confirm(`Удалить запись «${k.title}»? Это действие не отменить.`)) return
    try {
      await api.del(`/api/knowledge/${k.id}`)
      list.setData((cur) => cur?.filter((x) => x.id !== k.id))
      toast('Запись удалена', 'success')
    } catch (e) {
      toastError(e, 'Не удалось удалить')
    }
  }

  return (
    <div>
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <div className="relative w-full sm:w-72">
          <Icon name="search" size={14} className="pointer-events-none absolute top-1/2 left-2.5 -translate-y-1/2 text-subtle" />
          <input
            type="search"
            className="ctl h-8 pl-8"
            placeholder="Полнотекстовый поиск…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            aria-label="Поиск по базе знаний"
          />
        </div>
        <select className="ctl h-8 w-auto max-w-full" value={scope} onChange={(e) => setScope(e.target.value as '' | ScopeType)} aria-label="Область">
          <option value="">Любая область</option>
          {(Object.keys(SCOPE_LABELS) as ScopeType[]).map((s) => (
            <option key={s} value={s}>
              {SCOPE_LABELS[s]}
            </option>
          ))}
        </select>
        <select className="ctl h-8 w-auto max-w-full" value={kind} onChange={(e) => setKind(e.target.value as '' | KnowledgeKind)} aria-label="Вид записи">
          <option value="">Любой вид</option>
          {KINDS.map((k) => (
            <option key={k} value={k}>
              {KNOWLEDGE_KIND_LABELS[k]}
            </option>
          ))}
        </select>
        <Button variant="primary" icon="plus" className="ml-auto" onClick={() => setEditing('new')}>
          Новая запись
        </Button>
      </div>

      {list.error && !list.data ? (
        <ErrorBox error={list.error} onRetry={list.reload} />
      ) : !list.data ? (
        <Loading />
      ) : items.length === 0 ? (
        <Empty icon="search">{query ? 'Ничего не нашлось' : 'База знаний пуста'}</Empty>
      ) : (
        <div className="flex flex-col gap-2">
          {items.map((k) => {
            const open = expanded.has(k.id)
            const long = k.body.length > 360 || k.body.split('\n').length > 6
            return (
              <article key={k.id} className="rounded-[12px] border border-line bg-surface px-[18px] py-3.5 shadow-card">
                <header className="flex items-start gap-2">
                  <div className="min-w-0 flex-1">
                    <h3 className="text-[15px] leading-snug font-semibold break-words">
                      {k.pinned ? (
                        <span className="mr-1 inline-block align-[-2px] text-muted" title="Закреплено — агенты видят всегда">
                          <Icon name="pin" size={14} />
                        </span>
                      ) : null}
                      {k.title}
                    </h3>
                    <div className="mt-1 flex flex-wrap items-center gap-1.5">
                      <Badge tone={k.kind === 'incident' ? 'warn' : 'neutral'}>
                        {KNOWLEDGE_KIND_LABELS[k.kind] ?? k.kind}
                      </Badge>
                      <ScopeLabel type={k.scope_type} id={k.scope_id} />
                    </div>
                  </div>
                  <span className="flex shrink-0 items-center gap-0.5">
                    <IconButton
                      icon="pin"
                      size="sm"
                      label={k.pinned ? 'Открепить' : 'Закрепить'}
                      active={k.pinned}
                      onClick={() => void patch(k, { pinned: !k.pinned })}
                    />
                    <IconButton icon="edit" size="sm" label="Изменить запись" onClick={() => setEditing(k)} />
                    <IconButton icon="trash" size="sm" label="Удалить запись" onClick={() => void remove(k)} />
                  </span>
                </header>
                <div className={cx('relative mt-1.5', long && !open && 'max-h-[120px] overflow-hidden')}>
                  <Markdown text={k.body} className="text-[13px]" />
                  {long && !open ? (
                    <div className="pointer-events-none absolute inset-x-0 bottom-0 h-10 bg-gradient-to-t from-surface to-transparent" />
                  ) : null}
                </div>
                {long ? (
                  <button
                    type="button"
                    className="mt-1 text-[12px] text-accent hover:underline"
                    onClick={() =>
                      setExpanded((s) => {
                        const n = new Set(s)
                        if (n.has(k.id)) n.delete(k.id)
                        else n.add(k.id)
                        return n
                      })
                    }
                  >
                    {open ? 'Свернуть' : 'Показать полностью'}
                  </button>
                ) : null}
                <footer className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11.5px] text-subtle">
                  {k.author ? <CreatedBy value={k.author} /> : null}
                  <span title={formatDateTime(k.updated_at)}>обновлено {timeAgo(k.updated_at, now)}</span>
                  {k.source ? <span className="max-w-full min-w-0 truncate">источник: {k.source}</span> : null}
                </footer>
              </article>
            )
          })}
        </div>
      )}

      {editing ? (
        <KnowledgeModal
          initial={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={(k) =>
            list.setData((cur) => (cur ? (cur.some((x) => x.id === k.id) ? cur.map((x) => (x.id === k.id ? k : x)) : [k, ...cur]) : cur))
          }
        />
      ) : null}
    </div>
  )
}

function KnowledgeModal({ initial, onClose, onSaved }: { initial: Knowledge | null; onClose: () => void; onSaved: (k: Knowledge) => void }) {
  const [title, setTitle] = useState(initial?.title ?? '')
  const [body, setBody] = useState(initial?.body ?? '')
  const [kind, setKind] = useState<KnowledgeKind>(initial?.kind ?? 'note')
  const [scopeType, setScopeType] = useState<ScopeType>(initial?.scope_type ?? 'global')
  const [scopeId, setScopeId] = useState(initial?.scope_id ?? '')
  const [pinned, setPinned] = useState(initial?.pinned ?? false)
  const [source, setSource] = useState(initial?.source ?? '')
  const [busy, run] = useBusy()
  const f = { title: useId(), body: useId(), kind: useId(), source: useId(), pin: useId() }

  const save = () =>
    run(async () => {
      if (!title.trim() || !body.trim()) {
        toast('Нужны заголовок и текст', 'error')
        return
      }
      if (scopeType !== 'global' && !scopeId) {
        toast('Выбери, к чему относится запись', 'error')
        return
      }
      const payload = {
        title: title.trim(),
        body: body.trim(),
        kind,
        scope_type: scopeType,
        scope_id: scopeType === 'global' ? null : scopeId,
        pinned,
        source: source.trim(),
      }
      try {
        const k = initial
          ? await api.patch<Knowledge>(`/api/knowledge/${initial.id}`, payload)
          : await api.post<Knowledge>('/api/knowledge', payload)
        onSaved(k)
        toast(initial ? 'Запись обновлена' : 'Запись добавлена', 'success')
        onClose()
      } catch (e) {
        toastError(e, 'Не удалось сохранить')
      }
    })

  return (
    <Modal
      title={initial ? 'Изменить запись' : 'Новая запись'}
      onClose={onClose}
      width="max-w-2xl"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Отмена
          </Button>
          <Button variant="primary" loading={busy} onClick={() => void save()}>
            Сохранить
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-[1fr_170px]">
          <Field label="Заголовок" htmlFor={f.title}>
            <input id={f.title} className="ctl" value={title} onChange={(e) => setTitle(e.target.value)} data-autofocus />
          </Field>
          <Field label="Вид" htmlFor={f.kind}>
            <select id={f.kind} className="ctl" value={kind} onChange={(e) => setKind(e.target.value as KnowledgeKind)}>
              {KINDS.map((k) => (
                <option key={k} value={k}>
                  {KNOWLEDGE_KIND_LABELS[k]}
                </option>
              ))}
            </select>
          </Field>
        </div>
        <Field label="Текст" htmlFor={f.body} hint="Markdown: заголовки, списки, `код`, ссылки.">
          <textarea id={f.body} className="ctl min-h-[200px] font-mono text-[12px]" value={body} onChange={(e) => setBody(e.target.value)} />
        </Field>
        <ScopePicker
          type={scopeType}
          id={scopeId}
          allowAgent
          onChange={(t, i) => {
            setScopeType(t)
            setScopeId(i)
          }}
        />
        <div className="grid grid-cols-1 items-end gap-3 sm:grid-cols-[1fr_auto]">
          <Field label="Источник (необязательно)" htmlFor={f.source}>
            <input id={f.source} className="ctl" value={source} onChange={(e) => setSource(e.target.value)} placeholder="ссылка, файл, разговор…" />
          </Field>
          <label htmlFor={f.pin} className="inline-flex min-h-8 cursor-pointer items-center gap-1.5 text-[12.5px]">
            <input id={f.pin} type="checkbox" className="h-4 w-4" checked={pinned} onChange={(e) => setPinned(e.target.checked)} />
            Закрепить — агенты видят всегда
          </label>
        </div>
      </div>
    </Modal>
  )
}
